import { getSubscriptionStatus } from './revenueCat'

/**
 * The `path` query parameter is attacker-controllable, so a crafted sign-in
 * link could otherwise bounce the user to another site the moment they
 * authenticate. Resolving the candidate against our own origin and keeping
 * only the path also rejects the forms a leading-slash check misses, such as
 * `//host`, `/\host` and `javascript:` URLs.
 */
function toSameOriginPath(candidate: string): string | null {
  if (typeof window === 'undefined') return null
  if (!candidate.startsWith('/')) return null

  let resolved: URL
  try {
    resolved = new URL(candidate, window.location.origin)
  } catch {
    return null
  }
  if (resolved.origin !== window.location.origin) return null
  // `/..//evil.com` resolves inside our origin but normalises to a pathname
  // that is protocol-relative again once the router re-reads it.
  if (resolved.pathname.startsWith('//')) return null

  return `${resolved.pathname}${resolved.search}${resolved.hash}`
}

export async function getPostSignInPath({
  pathFromQuery,
  userId,
}: {
  pathFromQuery?: string | null
  userId: string | undefined
}): Promise<string> {
  // An unusable target falls through to the subscription-based destination
  // rather than reporting, so a crafted link cannot flood our error budget.
  const requestedPath = pathFromQuery ? toSameOriginPath(pathFromQuery) : null
  if (requestedPath) {
    return requestedPath
  }

  const subscriptionStatus = await getSubscriptionStatus(userId)

  switch (subscriptionStatus) {
    case 'active': {
      return '/account/subscription'
    }
    case 'inactive': {
      return '/checkout/plans'
    }
    case 'none': {
      return '/onboarding'
    }
    default: {
      throw new Error(
        `Unhandled subscription status: ${subscriptionStatus as string}`,
      )
    }
  }
}
